import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { IContact } from '../../../../shared/models/contact';
import { IAfterSchoolActivity } from '../../../../shared/models/afterschoolActivity';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ContactsService } from '../../../../shared/services/contact.service';
import { AfterschoolActivityService } from '../../../../shared/services/afterschoolActivity.service';
import { NoEmojiDirective } from '../../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'new-activity',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    FormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './new-activity.component.html',
  styleUrl: './new-activity.component.css'
})
export class NewActivityComponent implements OnInit {
  icons = Icons;
  currentBuildingId: string | null = null;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];

  @Input() showFullDetails: boolean = true;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  activity: IAfterSchoolActivity = {
    activity_name: '',
    location: '',
    primary_contact_id: 0,
    secondary_contact_id: 0,
    emergency_equipment: '',
    first_aid_contact_ids: [],
    cpr_contact_ids: [],
    aed_contact_ids: [],
    incident_reporting_contact_ids: [],
    ieap_contact_ids: [],
    emergency_diabetic_contact_ids: [],
    courses_of_action: ''
  };

  suggestions: string[] = [ 'First', 'Aid', 'Cell', 'AED', 'Landline', 'Two-Way Radio', 'Emergency Transportation', 'Weather Radio', 'Blood Borne Pathogens Kit' ];
  selectedContacts = {
    first_aid: [] as { id: number; name: string; email: string }[],
    cpr: [] as { id: number; name: string; email: string }[],
    aed: [] as { id: number; name: string; email: string }[],
    incident_reporting: [] as { id: number; name: string; email: string }[],
    ieap: [] as { id: number; name: string; email: string }[],
    emergency_diabetic: [] as { id: number; name: string; email: string }[]
  };

  loading: boolean = false;
  showSuggestions: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private afterschoolActivityService: AfterschoolActivityService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.loadContacts();
  }

  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList];

        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));

          this.activity.primary_contact_id = this.contactsList[0].id;
          this.activity.secondary_contact_id = this.contactsList[0].id;
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  }

  addContact(field: keyof typeof this.selectedContacts, contactId: string): void {
    const contact = this.contactsList.find(c => c.id === Number(contactId));
    if (contact && !this.selectedContacts[field].some(c => c.id === contact.id)) {
      this.selectedContacts[field].push({ id: contact.id, name: contact.name, email: contact.email });
      (this.activity[`${field}_contact_ids`] as number[]).push(contact.id);
    }
  }

  removeContact(field: keyof typeof this.selectedContacts, index: number): void {
    const removed = this.selectedContacts[field].splice(index, 1)[0];
    if (removed) {
      this.activity[`${field}_contact_ids`] = (this.activity[`${field}_contact_ids`] as number[]).filter(
        id => id !== removed.id
      );
    }
  }

  get filteredSuggestions(): string[] {
    const query = this.activity.emergency_equipment?.toLowerCase() || '';
    return this.suggestions.filter(s => s.toLowerCase().includes(query));
  }

  selectSuggestion(value: string): void {
    this.activity.emergency_equipment = value;
    this.showSuggestions = false;
  }

  hideSuggestions(): void {
    setTimeout(() => this.showSuggestions = false, 150);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  saveNew(): void {
    if (this.isValidActivity() && this.currentBuildingId) {
      this.loading = true;
      console.log("Submitting activity:", this.activity);
      this.afterschoolActivityService.createAfterschoolActivity(this.currentBuildingId, this.activity).subscribe({
        next: (res) => {
          this.loading = false;
          if (!res.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after creating new after school activity.' });
          }
        },
        error: (err) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: err.message });
        }
      })
    }
  }

  isValidActivity(): boolean {
    if (!this.activity.activity_name || !this.activity.courses_of_action) {
      return false;
    }
    // Location and equipment are only collected on the full (AL) form.
    if (this.showFullDetails) {
      return Boolean(this.activity.location && this.activity.emergency_equipment);
    }
    return true;
  }

}
